import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from "./components/Header";
import AppointmentDetails from "./components/Details";
import Locations from "./components/Locations";
import Dates from "./components/Dates";
import TimeSlots from "./components/TimeSlots";
import BasicInfo from "./components/BasicInfo"; 
import Demographics from "./components/Demographics";
import Confirmation from "./components/Confirmation";
import Navigation from "./components/Navigation"; 
import EventBooking from "./components/EventBooking";
import { LocationProvider } from "./components/LocationContext";
import Patient from "./models/Patient"; // Import Patient model
import ReschedulePage from './pages/ReschedulePage';
import CancelPage from './pages/CancelPage';
import { createPatient } from './services/patientService';
import { hasCompleteAddress } from './utils/hasCompleteAddress';

function App() {
  const [currentStep, setCurrentStep] = useState(1);
  const [location, setLocation] = useState("");
  const [selectedServiceType, setSelectedServiceType] = useState('primary_care');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedAppointmentId, setSelectedAppointmentId] = useState("");
  const [basicInfo, setBasicInfo] = useState({});      
  const [demographics, setDemographics] = useState({});
  const [confirmationConfirmed, setConfirmationConfirmed] = useState(false);
  const [currentPatient, setCurrentPatient] = useState(null); // Track patient data
  const [isBasicInfoValid, setIsBasicInfoValid] = useState(false);
  const [patientCheckFunction, setPatientCheckFunction] = useState(null);
  const [patientMatches, setPatientMatches] = useState([]);
  const [verifiedPatientDemographics, setVerifiedPatientDemographics] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verifiedPatientId, setVerifiedPatientId] = useState(null);

  // Helper function to properly capitalize names
  const formatName = (name) => {
    if (!name) return "";
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to format date for display (MM/DD/YYYY)
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const [yyyy, mm, dd] = dateString.split("-");
    return `${parseInt(mm, 10)}/${parseInt(dd, 10)}/${yyyy}`;
  };

  const handleLocationSelect = (selectedLocation) => {
    setLocation(selectedLocation);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleTimeSelect = (slot) => {
    setSelectedTime(slot.time);
    setSelectedAppointmentId(slot.id);
  };

  // Clear form data based on current step
  const clearFormData = (step) => {
    switch(step) {
      case 1:
        // Clear location data
        setLocation("");
        setSelectedServiceType('primary_care');
        break;
      case 2:
        // Clear date and time data
        setSelectedDate(null); // Add this line to clear the selected date
        setSelectedTime("");
        setSelectedAppointmentId("");
        break;
      case 3:
        // Clear basic info data
        setBasicInfo({});
        setIsBasicInfoValid(false);
        break;
      case 4:
        // Clear demographics data
        setDemographics({});
        break;
      case 5:
        // Clear confirmation data
        setConfirmationConfirmed(false);
        break;
      default:
        break;
    }
  };

  const handleBack = () => {
    // First clear data based on current step
    clearFormData(currentStep);
    
    // Then update the step
    setCurrentStep(currentStep - 1);
    
    // Force-clear time values when going back to step 2
    // This ensures time slots don't appear as selected
    if (currentStep === 3) {
      setSelectedDate(null);
      setSelectedTime("");
      setSelectedAppointmentId("");
    }
  };

  const handleNext = async () => {
    // Check for existing patients when navigating from BasicInfo to Demographics
    if (currentStep === 3 && patientCheckFunction) {
      console.log("🔍 Checking for existing patients before proceeding to Demographics...");
      try {
        // Call the function to check for patient matches
        const matches = await patientCheckFunction();
        setPatientMatches(matches);
        
        console.log(`✅ Patient check complete, found ${matches.length} potential matches.`);
        
        // Optional: You could add logic here to handle matches
        // For example, show a confirmation dialog if matches are found
        if (matches.length > 0) {
          console.log("⚠️ Potential duplicate patients found:", matches);
          // You could add additional handling here if needed
        }
      } catch (error) {
        console.error("❌ Error checking for patient matches:", error);
      }
    }
    
    // Special handling when moving from basic info to demographics (step 3 -> 4)
    if (currentStep === 3) {
      // Format names and date before creating patient object
      const formattedFirstName = formatName(basicInfo.firstName || "");
      const formattedLastName = formatName(basicInfo.lastName || "");
      const formattedDob = formatDateForDisplay(basicInfo.dob || "");
      
      // Create a patient object with the basic info collected
      const patientData = new Patient({
        firstName: formattedFirstName,
        lastName: formattedLastName,
        email: basicInfo.email || "",
        phone: basicInfo.phone || "",
        address: basicInfo.address || "",
        rawAddress: basicInfo.rawAddress || "",
        city: basicInfo.city || "",
        state: basicInfo.state || "",
        zip: basicInfo.zip || "",
        cityState: basicInfo.cityState || "",
        cityStateZip: basicInfo.cityStateZip || "",
        dob: formattedDob,         // Keep original format for calculations
        reason: basicInfo.reason || "",
        // Demographics fields will be empty initially
        gender: demographics.gender || "",
        race: demographics.race || "",
        maritalStatus: demographics.maritalStatus || "",
        ethnicity: demographics.ethnicity || "",
        income: demographics.income || "",
        familySize: demographics.familySize || ""
      });
      
      // Log the patient data
      console.log("Patient Basic Info Completed:");
      console.log(patientData);
      
      // Store the patient data for later use
      setCurrentPatient(patientData);
    }
    
    // Special handling when moving from demographics to confirmation (step 4 -> 5)
    else if (currentStep === 4 && currentPatient) {
      // Update the patient data with demographics
      const updatedPatient = new Patient({
        ...currentPatient,
        address: demographics.address || currentPatient.address || "",
        // Include all address components explicitly
        street: demographics.street || currentPatient.street || "",
        city: demographics.city || currentPatient.city || "",
        state: demographics.state || currentPatient.state || "",
        zip: demographics.zip || currentPatient.zip || "",
        cityState: demographics.cityState || currentPatient.cityState || "",
        cityStateZip: demographics.cityStateZip || currentPatient.cityStateZip || "",
        rawAddress: demographics.rawAddress || currentPatient.rawAddress || "",
        gender: demographics.gender || "",
        race: demographics.race || "",
        maritalStatus: demographics.maritalStatus || "",
        ethnicity: demographics.ethnicity || "",
        income: demographics.income || "",
        familySize: demographics.familySize || ""
      });
      
      // Log the updated patient data
      console.log("Patient Demographics Completed:");
      console.log(updatedPatient);
      console.log("Complete patient JSON:", updatedPatient.toJSON());
      
      // Update the patient data
      setCurrentPatient(updatedPatient);
    }
    
    // Move to the next step
    setCurrentStep(currentStep + 1);
  };

  const handleBasicInfoSubmit = (data) => {
    // Format names before submission
    const formattedData = {
      ...data,
      firstName: formatName(data.firstName || ""),
      lastName: formatName(data.lastName || ""),
      displayDob: formatDateForDisplay(data.dob || "")
    };
    
    console.log('Basic info submitted with formatted data:', formattedData);
    setBasicInfo(formattedData);
    setCurrentStep('demographics');
  };
  
  const handleDemographicsSubmit = async (data) => {
    setDemographics(data);
    
    try {
      // Create a new Patient with all the data, ensuring address fields are included
      const patientData = {
        ...basicInfo,
        ...data,
        // Make sure these fields are explicitly included
        address: data.address || '',
        street: data.street || '',
        city: data.city || '',
        state: data.state || '',
        zip: data.zip || '',
        cityStateZip: data.cityStateZip || '',
        rawAddress: data.rawAddress || ''
      };
      
      // Log to verify all data is present before creating the Patient
      console.log('Patient Demographics Completed:', patientData);
      
      // Create and save the patient
      const patient = await createPatient(patientData);
      
      // Success - patient was created with all address fields
      console.log('Patient created successfully:', patient);
      setCurrentStep('confirmation');
    } catch (error) {
      console.error('Error saving patient:', error);
      // Handle error
    }
  };

  // Validation helper for current step
  const validateStep = () => {
    if (currentStep === 1) {
      // Step 1 requires a location AND a service type selection
      return !!location && !!selectedServiceType;
    } else if (currentStep === 2) {
      // Step 2 requires a date and time selection
      return selectedDate && selectedTime;
    } else if (currentStep === 3) {
      // Step 3 requires basic info fields and age validation
      const isAllFieldsFilled = (
        basicInfo.firstName &&
        basicInfo.lastName &&
        basicInfo.dob &&
        basicInfo.email &&
        basicInfo.phone &&
        basicInfo.reason 
      );
      
      // Check if patient is at least 18 years old
      const isAdult = Patient.isAdult(basicInfo.dob);
      
      return isAllFieldsFilled && isAdult;
    } else if (currentStep === 4) {
      // Step 4 requires Demographics fields plus a complete address
      return (
        hasCompleteAddress(demographics) &&
        demographics.gender &&
        demographics.race &&
        demographics.maritalStatus &&
        demographics.ethnicity &&
        demographics.income
      );
    }
    return true;
  };

  const handleBasicInfoChange = (updatedInfo) => {
    setBasicInfo(updatedInfo);
  };

  const handleValidityChange = (isValid) => {
    setIsBasicInfoValid(isValid);
  };

  const handleContinue = () => {
    if (isBasicInfoValid) {
      setCurrentStep('demographics');
    }
  };

  // This function receives either a function reference or match results
  const handlePatientCheckMatches = (data) => {
    if (data.type === 'FUNCTION_REFERENCE') {
      console.log("📝 Received patient check function reference");
      setPatientCheckFunction(data.checkFunction);
    } else if (data.type === 'MATCH_VERIFIED') {
      console.log("👤 Received verified patient match:", data.verifiedPatient);
      
      // Store the verified patient ID
      setVerifiedPatientId(data.verifiedPatient.id);
      
      // Store demographics data to pre-fill the Demographics component
      if (data.demographicsData) {
        console.log("📋 Pre-filling demographics with:", data.demographicsData);
        setVerifiedPatientDemographics(data.demographicsData);
        setDemographics(data.demographicsData);
        setShowVerificationModal(true);
      }
    } else {
      console.warn("⚠️ Received unknown data type in handlePatientCheckMatches");
    }
  };

  return (
    <LocationProvider>
      <Router>
        <Routes>
          <Route path="/reschedule" element={<ReschedulePage />} />
          <Route path="/cancel" element={<CancelPage />} />
          <Route path="/book" element={<EventBooking />} />
          <Route path="/" element={
            <div style={{ fontFamily: "Quicksand", backgroundColor: "#f9f9f9", minHeight: "100vh" }}>
              <Header />
              <div style={{ maxWidth: "800px", margin: "0 auto", padding: "1rem" }}>
                <AppointmentDetails />
                
                {currentStep === 1 && <Locations onSelect={handleLocationSelect} onServiceTypeSelect={setSelectedServiceType} />}
                {currentStep === 2 && (
                  <>
                    <Dates onSelect={handleDateSelect} />
                    <TimeSlots 
                      onSelect={handleTimeSelect} 
                      selectedTime={selectedTime} 
                      selectedDate={selectedDate} 
                    />
                  </>
                )}
                {currentStep === 3 && (
                  <>
                    <BasicInfo 
                      basicInfo={basicInfo} 
                      onChange={handleBasicInfoChange} 
                      onValidityChange={handleValidityChange} 
                      onCheckPatientMatches={handlePatientCheckMatches}
                    />
                  </>
                )}
                {currentStep === 4 && (
                  <Demographics 
                    demographics={demographics} 
                    onChange={setDemographics} 
                    showVerificationModal={showVerificationModal}
                    onVerificationModalClose={() => setShowVerificationModal(false)}
                  />
                )}
                {currentStep === 5 && (
                  <Confirmation
                    appointmentId={selectedAppointmentId}
                    location={location}
                    selectedDate={selectedDate}
                    selectedTime={selectedTime}
                    selectedServiceType={selectedServiceType}
                    patientData={currentPatient} // Pass patient data to Confirmation
                    verifiedPatientId={verifiedPatientId} // Pass verified patient ID
                    onConfirmed={() => setConfirmationConfirmed(true)}
                  />
                )}
                
                <Navigation 
                  currentStep={currentStep} 
                  onBack={handleBack} 
                  onNext={handleNext}
                  maxStep={5}
                  nextDisabled={!validateStep()}
                  confirmationConfirmed={confirmationConfirmed}
                />
              </div>
            </div>
          } />
        </Routes>
      </Router>
    </LocationProvider>
  );
}

export default App;